import { t } from "@/i18n-js/instance";
import type {
  Space,
  Student,
} from "@/react/components/Spaces/CourseSpace/AdminSummary/Engagement/types";
import { CurrentSpaceContextProvider } from "@/react/contexts";
import {
  ModalManager,
  useModal,
} from "@circle-react/components/Layout/ModalManagerProvider";
import { Modal } from "@circle-react-shared/uikit/ModalV2";
import { Typography } from "@circle-react-shared/uikit/Typography";
import { Curriculum } from "./Curriculum";
import { Header } from "./Header";
import { SkeletonLoader } from "./SkeletonLoader";
import { useStudentProgress } from "./useStudentProgress";

interface ModalProps {
  space: Space;
  student: Student;
}

const StudentProgressModalComponent = ({ student, space }: ModalProps) => {
  const modal = useModal();
  const { curriculum, isLoading } = useStudentProgress(student.id, space.id);

  return (
    <Modal
      title={t("courses.admin_summary.student_progress_modal.title")}
      isOpen={modal.visible}
      onClose={modal.remove}
    >
      <CurrentSpaceContextProvider spaceId={space.id}>
        <Modal.Overlay />
        <Modal.Content size="lg">
          <Modal.Header>
            <div className="flex w-full justify-between">
              <Typography.TitleMd>
                {t("courses.admin_summary.student_progress_modal.title")}
              </Typography.TitleMd>
              <div className="flex items-center">
                <Modal.CloseButton onClick={modal.hide} />
              </div>
            </div>
          </Modal.Header>
          <Modal.Body className="my-2">
            <Header student={student} />
            {isLoading ? (
              <SkeletonLoader rows={6} />
            ) : (
              <Curriculum curriculum={curriculum} />
            )}
          </Modal.Body>
        </Modal.Content>
      </CurrentSpaceContextProvider>
    </Modal>
  );
};

const StudentProgressModal = ModalManager.create(
  ({ student, space }: ModalProps) => (
    <StudentProgressModalComponent student={student} space={space} />
  ),
);

export const useStudentProgressModal = () => useModal(StudentProgressModal);
